/**
 * Ayni PDP Gallery Dots
 * -----------------------------------------------------------------------------
 * Pagination-Punkte für das mobile Produkt-Karussell des Blum-Themes.
 *
 * Bewusst entkoppelt von der internen Blum-Carousel-API (SHTCarousel):
 * gelesen wird ausschliesslich der Scroll-Zustand des Scroll-Containers
 * (.js-carousel-items). Damit überlebt die Komponente Theme-Updates.
 *
 * Verhalten:
 *  - Punkte werden aus den sichtbaren Slides erzeugt (Varianten-Slides mit
 *    d-none werden ignoriert).
 *  - Aktiver Punkt folgt dem Scroll-Zustand (auch bei Wischen).
 *  - Klick scrollt zum jeweiligen Slide (respektiert prefers-reduced-motion).
 *  - MutationObserver deckt Varianten-Wechsel und AJAX-Section-Reloads ab.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('ayni-gallery-dots')) return;

  var MOBILE_MAX_WIDTH = 768;

  function rafThrottle(fn) {
    var scheduled = false;
    return function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        fn();
      });
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class AyniGalleryDots extends HTMLElement {
    connectedCallback() {
      this.carousel = document.getElementById(this.dataset.carouselId);
      this.track = this.carousel ? this.carousel.querySelector('.js-carousel-items') : null;
      if (!this.track) return;

      this.labelTemplate = this.dataset.labelTemplate || 'Bild __INDEX__';
      this.slideCount = -1;

      this.handleScroll = rafThrottle(this.updateActiveDot.bind(this));
      this.handleResize = rafThrottle(this.build.bind(this));
      this.handleMutation = rafThrottle(this.build.bind(this));

      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);
      this.addEventListener('click', this.onClick.bind(this));

      this.observer = new MutationObserver(this.handleMutation);
      this.observer.observe(this.track, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      this.build();
    }

    disconnectedCallback() {
      if (this.track) {
        this.track.removeEventListener('scroll', this.handleScroll);
      }
      window.removeEventListener('resize', this.handleResize);
      if (this.observer) this.observer.disconnect();
    }

    /** Sichtbare Slides – Blum blendet Varianten-Slides per d-none aus. */
    getSlides() {
      return Array.prototype.filter.call(this.track.children, function (el) {
        return el.clientWidth > 0;
      });
    }

    build() {
      if (window.innerWidth > MOBILE_MAX_WIDTH) {
        // Desktop: Punkte werden per CSS ausgeblendet, DOM bleibt leer.
        if (this.childElementCount) this.replaceChildren();
        this.slideCount = -1;
        return;
      }

      var slides = this.getSlides();
      if (slides.length < 2) {
        if (this.childElementCount) this.replaceChildren();
        this.slideCount = slides.length;
        return;
      }

      if (slides.length !== this.slideCount) {
        var fragment = document.createDocumentFragment();
        for (var i = 0; i < slides.length; i++) {
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'ayni-gallery-dots__dot';
          button.dataset.index = String(i);
          button.setAttribute('aria-label', this.labelTemplate.replace('__INDEX__', String(i + 1)));
          fragment.appendChild(button);
        }
        this.replaceChildren(fragment);
        this.slideCount = slides.length;
      }

      this.updateActiveDot();
    }

    getActiveIndex(slides) {
      var scrollLeft = this.track.scrollLeft;
      var activeIndex = 0;
      var smallestDelta = Infinity;

      for (var i = 0; i < slides.length; i++) {
        var delta = Math.abs(slides[i].offsetLeft - this.track.offsetLeft - scrollLeft);
        if (delta < smallestDelta) {
          smallestDelta = delta;
          activeIndex = i;
        }
      }
      return activeIndex;
    }

    updateActiveDot() {
      var dots = this.children;
      if (!dots.length) return;

      var slides = this.getSlides();
      if (slides.length !== dots.length) {
        this.build();
        return;
      }

      var activeIndex = this.getActiveIndex(slides);
      for (var i = 0; i < dots.length; i++) {
        if (i === activeIndex) {
          dots[i].setAttribute('aria-current', 'true');
        } else {
          dots[i].removeAttribute('aria-current');
        }
      }
    }

    onClick(event) {
      var dot = event.target.closest('.ayni-gallery-dots__dot');
      if (!dot) return;

      var slides = this.getSlides();
      var slide = slides[parseInt(dot.dataset.index, 10)];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }
  }

  customElements.define('ayni-gallery-dots', AyniGalleryDots);
})();